import { useMemo, useState } from "react";
import { type SetURLSearchParams } from "react-router";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";

import { ChevronLeftIcon, FilterListIcon, XIcon } from "@prequel/shared-components";

export type FilterField = {
  key: string;
  label: string;
  queryParam: string;
  options: { value: string; label: string }[];
  loading?: boolean;
  radio?: boolean;
};

type ActiveFilter = { key: string; value: string };

type FilterBarProps = {
  filterFields: FilterField[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
};

const FilterBar = ({ filterFields, searchParams, setSearchParams }: FilterBarProps) => {
  const [activeField, setActiveField] = useState<FilterField | null>(null);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Derive active filters from URL search params
  const filters: ActiveFilter[] = useMemo(() => {
    const result: ActiveFilter[] = [];
    for (const field of filterFields) {
      const value = searchParams.get(field.queryParam);
      if (value) {
        result.push({ key: field.key, value });
      }
    }
    return result;
  }, [searchParams, filterFields]);

  const activeKeys = new Set(filters.map((f) => f.key));

  const applySelectFilter = (field: FilterField, values: string[], close: () => void) => {
    if (values.length > 0) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(field.queryParam, field.radio ? values[0] : values.join(","));
          params.delete("cursor");
          return params;
        },
        { replace: true },
      );
      setActiveField(null);
      setSelectedValues([]);
      setSearchQuery("");
      close();
    }
  };

  const removeFilter = (key: string) => {
    const field = filterFields.find((ff) => ff.key === key);
    if (field) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete(field.queryParam);
          params.delete("cursor");
          return params;
        },
        { replace: true },
      );
    }
  };

  const clearAllFilters = () => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        for (const field of filterFields) {
          params.delete(field.queryParam);
        }
        params.delete("cursor");
        return params;
      },
      { replace: true },
    );
  };

  const hasFilters = filters.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Popover className="relative">
        {({ close }) => (
          <>
            <PopoverButton
              className="
                flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700
                outline-none
                hover:text-gray-900
              "
              onClick={() => {
                setActiveField(null);
                setSelectedValues([]);
                setSearchQuery("");
              }}
            >
              <FilterListIcon className="size-4 shrink-0 text-gray-500" />
              Filter and search
            </PopoverButton>
            <PopoverPanel className="absolute left-0 z-10 mt-2 w-64 rounded-md bg-white shadow-lg ring-1 ring-black/5">
              {!activeField ? (
                <div className="py-1">
                  {filterFields.map((field) => {
                    const isActive = activeKeys.has(field.key);
                    return (
                      <button
                        key={field.key}
                        disabled={isActive}
                        className={`
                          block w-full px-3 py-2 text-left text-sm transition-colors
                          ${
                            isActive
                              ? "cursor-not-allowed text-gray-300"
                              : `
                                text-gray-700
                                hover:bg-gray-50
                              `
                          }
                        `}
                        onClick={() => {
                          setActiveField(field);
                          setSelectedValues([]);
                          setSearchQuery("");
                        }}
                      >
                        {field.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <>
                  <button
                    className="
                      flex w-full items-center gap-1 border-b border-gray-100 px-3 py-1.5 text-xs text-gray-400
                      transition-colors
                      hover:text-gray-600
                    "
                    onClick={() => {
                      setActiveField(null);
                      setSelectedValues([]);
                      setSearchQuery("");
                    }}
                  >
                    <ChevronLeftIcon className="size-3 shrink-0" />
                    {activeField.label}
                  </button>
                  <div className="border-b border-gray-100 px-3 py-2">
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="
                        w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 transition-colors
                        outline-none
                        placeholder:text-gray-400
                        focus:border-gray-400
                      "
                    />
                  </div>
                  {activeField.loading ? (
                    <div className="p-3 text-center text-sm text-gray-400">Loading…</div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto py-1">
                      {activeField.options
                        .filter((opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map((opt) => (
                          <label
                            key={opt.value}
                            className="
                              flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700
                              transition-colors
                              hover:bg-gray-50
                            "
                          >
                            <input
                              type={activeField.radio ? "radio" : "checkbox"}
                              name={activeField.radio ? activeField.key : undefined}
                              checked={selectedValues.includes(opt.value)}
                              onChange={() =>
                                setSelectedValues((prev) =>
                                  activeField.radio
                                    ? [opt.value]
                                    : prev.includes(opt.value)
                                      ? prev.filter((v) => v !== opt.value)
                                      : [...prev, opt.value],
                                )
                              }
                              className={`
                                border-gray-300 text-gray-900
                                focus:ring-gray-900
                                ${activeField.radio ? "" : "rounded-sm"}
                              `}
                            />
                            {opt.label}
                          </label>
                        ))}
                      {activeField.options.filter((opt) => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
                        .length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No matches</div>}
                    </div>
                  )}
                  <div className="border-t border-gray-100 px-3 py-2">
                    <button
                      onClick={() => applySelectFilter(activeField, selectedValues, close)}
                      disabled={selectedValues.length === 0}
                      className="
                        w-full rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white transition-colors
                        hover:bg-gray-800
                        disabled:cursor-not-allowed disabled:opacity-40
                      "
                    >
                      Apply{selectedValues.length > 0 ? ` (${selectedValues.length})` : ""}
                    </button>
                  </div>
                </>
              )}
            </PopoverPanel>
          </>
        )}
      </Popover>
      {filters.map((f) => {
        const field = filterFields.find((ff) => ff.key === f.key);
        return (
          <div
            key={f.key}
            className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white py-1 pr-2.5 pl-1.5"
          >
            <button onClick={() => removeFilter(f.key)} className="hover:text-gray-600">
              <XIcon className="size-3.5 text-gray-400" />
            </button>
            <span className="text-sm text-gray-500">{field?.label}:</span>
            <span className="text-sm font-medium text-blue-600">
              {f.value
                .split(",")
                .map((v) => field?.options.find((opt) => opt.value === v)?.label ?? v)
                .join(", ")}
            </span>
          </div>
        );
      })}
      {hasFilters && (
        <button
          onClick={clearAllFilters}
          className="
            text-sm text-gray-400 underline underline-offset-2 transition-colors
            hover:text-gray-600
          "
        >
          Clear filters
        </button>
      )}
    </div>
  );
};

export default FilterBar;
